<template>
    <div id="contact" class="page-content">
        <div class="container">
            <div class="heading">
                <h1>{{ page.title }}</h1>
                <p>{{ page.subtitle }}</p>
            </div>
        </div>

        <div class="map-container">
            <client-only>
                <l-map
                    :zoom="15"
                    :center="[locations[0].lat, locations[0].lng]"
                    style="height: 400px; width: 100%"
                >
                    <l-tile-layer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; <a href='https://www.openstreetmap.org/copyright' target='_blank' rel='noopener'>OpenStreetMap</a> contributors"
                    />
                    <l-marker
                        ref="marker"
                        :lat-lng="[locations[0].lat, locations[0].lng]"
                        @ready="openPopup"
                    >
                        <l-popup>
                            <div class="info-window">
                                <h4>
                                    <i class="el-icon-place" />
                                    Metropolitan Arborist
                                </h4>
                                <p>
                                    1/322 Huntingdale Rd Mount Waverley,
                                    Victoria 3149
                                </p>
                            </div>
                        </l-popup>
                    </l-marker>
                </l-map>
            </client-only>
        </div>

        <div class="full-width">
            <div class="container">
                <div class="form-container">
                    <h3>Send us an enquiry</h3>
                    <p>We will get back to you as soon as possible!</p>

                    <el-form
                        ref="contactForm"
                        v-loading="loading"
                        :model="contactForm"
                        :rules="rules"
                        label-position="top"
                        @submit.prevent.native="submitForm"
                    >
                        <el-row :gutter="20">
                            <el-col :xs="24">
                                <el-form-item prop="name" label="Name">
                                    <el-input v-model="contactForm.name">
                                    </el-input>
                                </el-form-item>
                            </el-col>

                            <el-col :xs="24">
                                <el-form-item prop="email" label="Email">
                                    <el-input
                                        v-model="contactForm.email"
                                        type="email"
                                    >
                                    </el-input>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24">
                                <el-form-item prop="phone" label="Phone">
                                    <el-input v-model="contactForm.phone">
                                    </el-input>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24">
                                <el-form-item prop="message" label="Message">
                                    <el-input
                                        v-model="contactForm.message"
                                        type="textarea"
                                        :autosize="{ minRows: 6, maxRows: 8 }"
                                    ></el-input>
                                </el-form-item>
                            </el-col>
                        </el-row>

                        <div class="altcha-wrapper">
                            <client-only>
                                <altcha-widget
                                    v-if="altchaChallenge"
                                    ref="altchaWidget"
                                    :challenge="altchaChallenge"
                                    hidefooter
                                    auto="onload"
                                    @verified="onAltchaVerified"
                                />
                                <div v-else class="altcha-placeholder" />
                            </client-only>
                        </div>

                        <el-form-item :style="{ marginBottom: '0' }">
                            <el-button type="primary" native-type="submit">
                                Send
                                <i class="el-icon-finished" />
                            </el-button>
                        </el-form-item>
                    </el-form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    async asyncData({ app, store, error }) {
        await store
            .dispatch('general/getPage', app.context.route.name)
            .catch((e) => {
                error({
                    statusCode: e.response.status,
                    message: 'Page not found'
                })
            })
    },

    data() {
        return {
            locations: [
                {
                    lat: -37.877059,
                    lng: 145.1080493
                }
            ],
            contactForm: {
                name: '',
                email: '',
                phone: '',
                message: '',
                captcha: ''
            },
            rules: {
                name: {
                    required: true,
                    message: 'This field is required',
                    trigger: 'blur'
                },
                email: [
                    {
                        required: true,
                        message: 'This field is required',
                        trigger: 'blur'
                    },
                    {
                        type: 'email',
                        message: 'Please enter valid email',
                        trigger: 'blur'
                    }
                ],
                message: {
                    required: true,
                    message: 'This field is required',
                    trigger: 'blur'
                }
            },
            loading: false,
            altchaChallenge: null,
            altchaPayload: null
        }
    },

    head() {
        return {
            title: this.page.title,
            meta: [
                {
                    hid: 'title',
                    name: 'title',
                    content: this.page.meta_title
                },
                {
                    hid: 'description',
                    name: 'description',
                    content: this.page.meta_description
                }
            ]
        }
    },

    computed: {
        ...mapGetters({
            page: 'general/page'
        })
    },

    mounted() {
        this.loadAltchaChallenge()
    },

    methods: {
        onAltchaVerified(e) {
            this.altchaPayload = e.detail?.payload || null
        },
        async loadAltchaChallenge() {
            const saltBytes = crypto.getRandomValues(new Uint8Array(12))
            const salt = Array.from(saltBytes)
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('')
            const maxnumber = 100000
            const number = Math.floor(Math.random() * maxnumber)
            const hashBuffer = await crypto.subtle.digest(
                'SHA-256',
                new TextEncoder().encode(salt + number)
            )
            const challenge = Array.from(new Uint8Array(hashBuffer))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('')
            this.altchaChallenge = JSON.stringify({
                algorithm: 'SHA-256',
                challenge,
                maxnumber,
                salt,
                signature: ''
            })
        },
        openPopup() {
            this.$refs.marker.mapObject.openPopup()
        },
        submitForm() {
            const altchaPayload = this.altchaPayload

            if (!altchaPayload) {
                this.$message({
                    message:
                        'Please wait for the CAPTCHA verification to complete',
                    type: 'warning'
                })
                return
            }

            this.contactForm.captcha = altchaPayload

            this.$refs.contactForm.validate((valid) => {
                if (valid) {
                    this.loading = true
                    this.$store
                        .dispatch('general/contact', this.contactForm)
                        .then((resp) => {
                            this.loading = false
                            this.$message({
                                message: resp.message,
                                type: 'success'
                            })
                            this.contactForm = {
                                name: '',
                                email: '',
                                phone: '',
                                message: '',
                                captcha: ''
                            }
                            this.altchaPayload = null
                            this.altchaChallenge = null
                            this.loadAltchaChallenge()
                        })
                        .catch((error) => {
                            this.loading = false
                            const errors = error.response?.data?.errors
                            if (errors) {
                                const first = Object.values(errors)[0]
                                this.$message({
                                    message: first[0],
                                    type: 'error'
                                })
                            } else {
                                this.$message({
                                    message:
                                        'Something went wrong. Please try again.',
                                    type: 'error'
                                })
                            }
                        })
                } else {
                    return false
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.map-container {
    position: relative;

    .contacts {
        position: absolute;
        z-index: 1;
        top: 50%;
        left: 10%;
        transform: translateY(-50%);
        display: inline-block;
        background: $white;
        padding: 3em;
        -webkit-box-shadow: 0px 0px 21px 0px rgba(0, 0, 0, 0.1);
        -moz-box-shadow: 0px 0px 21px 0px rgba(0, 0, 0, 0.1);
        box-shadow: 0px 0px 21px 0px rgba(0, 0, 0, 0.1);
        border-radius: 4px;

        h2 {
            margin: 0;
            color: $primary;
        }

        i {
            margin-right: 1em;
            color: $primary;
        }

        a {
            display: flex;
            align-items: baseline;
            margin-top: 1.5em;
            color: $body;
        }

        span {
            line-height: initial;
        }
    }
}

.full-width {
    display: inline-block;
    width: 100%;
    background: $floral-white;
    padding: 3em 0;
    background-image: url('~assets/images/ovale.svg');
    background-repeat: no-repeat;
    background-size: auto 167%;
    background-position: -109% 141%;
}

.form-container {
    max-width: 500px;
    margin: 0 auto;

    h3 {
        text-align: center;
        box-sizing: inherit;
        border: 0px solid;
        margin-bottom: 0px;
        font-size: 1.125rem;
        color: rgb(0, 0, 0);
    }

    p {
        text-align: center;
        color: rgb(107, 165, 74);
        font-weight: normal;
        font-size: 14px;
        line-height: 26px;
        white-space: pre;
    }
}

.altcha-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;

    altcha-widget,
    .altcha-placeholder {
        --altcha-color-base: #ffffff;
        --altcha-color-border: #dcdfe6;
        --altcha-color-text: #606266;
        --altcha-color-check: $primary;
        width: 100%;
        max-width: 340px;
    }

    .altcha-placeholder {
        height: 54px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background: #ffffff;
    }
}

@media only screen and (max-width: 991px) {
    .map-container {
        .contacts {
            left: 2rem;
        }
    }
    .full-width {
        padding: 2em 0;
    }
}

@media only screen and (max-width: 767px) {
    .map-container {
        .contacts {
            position: initial;
            padding: 2em;
            width: 100%;
            box-shadow: none;
            transform: none;
        }
    }
}
</style>
